import { useEffect } from "react";
import { Link } from "react-router-dom";
import { twentyFourHoursToLiveEpisodes, RESUME_URL } from "@/data";

const HERO_IMAGE =
  "https://customer-assets.emergentagent.com/job_63deb20a-37d0-451f-872c-c4c59d1b6116/artifacts/p6736a46_NLE_CHOPPA_THUMB003.png";

const KEY_CONTRIBUTIONS = [
  "Led visual execution and creative direction across the series",
  "Designed and implemented the graphics package and motion language",
  "Built a repeatable AI-assisted workflow for episodic production",
  "Solved technical and creative challenges across episodes in real time",
  "Ensured consistency in character likeness, lighting, and visual tone",
  "Helped elevate the final output beyond early internal attempts",
];

export default function TwentyFourHoursToLive() {
  useEffect(() => {
    window.scrollTo(0, 0);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("revealed");
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -100px 0px" }
    );

    document.querySelectorAll(".reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="App thtl-page">
      {/* Top Nav */}
      <div className="cs-topnav" data-testid="cs-topnav">
        <Link to="/" className="cs-back" data-testid="cs-back-link">
          <span className="cs-back-arrow">←</span>
          <span>Dan McNamara</span>
        </Link>
        <div className="nav-links">
          <Link to="/#reel">Reel</Link>
          <Link to="/#case-studies">Case Studies</Link>
          <Link to="/#work">Work</Link>
          <Link to="/#contact">Contact</Link>
          <a
            href={RESUME_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="nav-resume"
            data-testid="nav-resume"
          >
            Resume
          </a>
        </div>
      </div>

      {/* Hero */}
      <section className="thtl-hero" data-testid="thtl-hero">
        <div className="thtl-hero-grid">
          <div className="thtl-hero-text">
            <p className="thtl-kicker reveal">
              <span className="thtl-pulse"></span>
              Case Study · Complex
            </p>
            <h1 className="thtl-headline reveal" data-testid="thtl-headline">
              24 Hours
              <br />
              to Live.
            </h1>
            <p className="thtl-tagline reveal">An AI-Driven Social Series</p>
            <div className="thtl-divider reveal"></div>
            <p className="thtl-summary reveal">
              A performance-driven social series for{" "}
              <strong>Complex</strong>, designed for TikTok and Instagram. My
              role focused on execution, creative direction, and building the
              workflow that made the series possible at a high level and at
              scale.
            </p>
          </div>

          <div className="thtl-hero-art reveal" aria-hidden="true">
            <img src={HERO_IMAGE} alt="24 Hours to Live — NLE Choppa key art" />
          </div>
        </div>
      </section>

      {/* Meta strip */}
      <section className="thtl-meta-strip reveal" data-testid="thtl-meta-strip">
        <div className="thtl-meta-grid">
          <div className="thtl-meta-item">
            <span className="thtl-meta-label">Client</span>
            <span className="thtl-meta-value">Complex</span>
          </div>
          <div className="thtl-meta-item">
            <span className="thtl-meta-label">Role</span>
            <span className="thtl-meta-value">
              Visual Execution &amp; Creative Direction
            </span>
          </div>
          <div className="thtl-meta-item">
            <span className="thtl-meta-label">Timeline</span>
            <span className="thtl-meta-value">Mar – Nov 2025</span>
          </div>
          <div className="thtl-meta-item">
            <span className="thtl-meta-label">Platforms</span>
            <span className="thtl-meta-value">TikTok · Instagram</span>
          </div>
        </div>
      </section>

      {/* Role */}
      <section
        id="role"
        className="thtl-section thtl-role reveal"
        data-testid="thtl-role"
      >
        <div className="thtl-section-header">
          <span className="section-number">01</span>
          <h2 className="thtl-section-title">Role</h2>
        </div>

        <div className="thtl-role-body">
          <div className="thtl-role-copy">
            <p>
              The original concept existed, but there was no clear path to
              producing it at a high level or at scale.
            </p>
            <p>
              I led the visual execution across the series — including logo,
              graphics package, motion, pacing, and overall visual identity —
              while also developing a custom AI-assisted image-to-video
              workflow that could integrate into a professional post-production
              pipeline.
            </p>
          </div>

          <ul
            className="thtl-contributions"
            aria-label="Key contributions"
            data-testid="thtl-contributions"
          >
            <li className="thtl-contributions-heading">Key Contributions</li>
            {KEY_CONTRIBUTIONS.map((c, i) => (
              <li key={i} className="thtl-contribution">
                <span className="thtl-contribution-dot" aria-hidden="true" />
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Process */}
      <section
        id="process"
        className="thtl-section thtl-process reveal"
        data-testid="thtl-process"
      >
        <div className="thtl-section-header">
          <span className="section-number">02</span>
          <h2 className="thtl-section-title">Process</h2>
        </div>

        <div className="thtl-process-copy">
          <p className="thtl-lead">
            This series was built before tools like Seedance were widely
            available, requiring a custom approach to AI-assisted production.
          </p>
          <p>
            The core challenge was not just generating visuals, but building a
            workflow that could deliver consistent, high-quality results under
            real production timelines.
          </p>
          <p>
            I developed and refined a pipeline that combined AI-generated
            imagery with traditional post-production techniques — allowing us
            to iterate quickly while maintaining control over quality,
            continuity, and brand safety.
          </p>
          <p>
            The result was a system that could move at the speed of social
            while still delivering cinematic, cohesive episodes.
          </p>
        </div>
      </section>

      {/* Episodes */}
      <section
        id="episodes"
        className="thtl-section thtl-episodes reveal"
        data-testid="thtl-episodes"
      >
        <div className="thtl-section-header">
          <span className="section-number">03</span>
          <h2 className="thtl-section-title">Episodes</h2>
          <span className="thtl-section-meta">
            {twentyFourHoursToLiveEpisodes.length} guests · Vertical · 9:16
          </span>
        </div>

        <div className="thtl-ep-grid">
          {twentyFourHoursToLiveEpisodes.map((ep, idx) => (
            <a
              key={ep.id}
              href={ep.tiktokUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={`thtl-ep ${ep.featured ? "thtl-ep-featured" : ""}`}
              data-testid={`thtl-episode-${ep.id}`}
            >
              <div className="thtl-ep-thumb">
                <img src={ep.image} alt={`${ep.guest} — 24 Hours to Live`} />
                <div className="thtl-ep-overlay">
                  <span className="thtl-ep-watch">Watch on TikTok →</span>
                </div>
              </div>
              <div className="thtl-ep-meta">
                <span className="thtl-ep-num">
                  EP {String(idx + 1).padStart(2, "0")}
                </span>
                <h3 className="thtl-ep-name">{ep.guest}</h3>
                <p className="thtl-ep-summary">{ep.summary}</p>
              </div>
            </a>
          ))}
        </div>
      </section>

      {/* Results */}
      <section
        id="results"
        className="thtl-section thtl-results reveal"
        data-testid="thtl-results"
      >
        <div className="thtl-section-header">
          <span className="section-number">04</span>
          <h2 className="thtl-section-title">Results</h2>
        </div>

        <div className="thtl-results-body">
          <p className="thtl-results-line">
            <span className="thtl-results-tag">Social-native by design.</span>{" "}
            Built and edited for vertical, sound-on, mobile-first viewing —
            pacing, hook structure, and graphic language tuned to platform
            behavior, not adapted from horizontal formats.
          </p>
          <p className="thtl-results-line">
            <span className="thtl-results-tag">Performance-driven.</span> Each
            new episode incorporated learnings from the last — what hooked,
            what held, what got shared — turning the series into a continuously
            optimized format rather than a fixed package.
          </p>
          <p className="thtl-results-line">
            <span className="thtl-results-tag">Portfolio significance.</span>{" "}
            One of the first publisher-led series at this scale to
            operationalize an AI image-to-video pipeline as the core production
            method — proving creative direction and AI workflows can ship a
            consistent, brand-safe show on a social-native cadence.
          </p>
        </div>
      </section>

      {/* Outro */}
      <section className="thtl-outro reveal" data-testid="thtl-outro">
        <div className="thtl-outro-inner">
          <h3 className="thtl-outro-title">
            Looking to build a social-native series, an AI workflow, or a show
            package that actually performs?
          </h3>
          <div className="thtl-outro-actions">
            <a
              href="mailto:dbmcnamara@gmail.com"
              className="thtl-outro-email"
              data-testid="thtl-outro-email"
            >
              dbmcnamara@gmail.com
            </a>
            <Link
              to="/"
              className="thtl-outro-back"
              data-testid="thtl-outro-back-link"
            >
              ← Back to Work
            </Link>
          </div>
        </div>
      </section>

      <footer className="site-footer">
        <p>© 2026 Dan McNamara. Available for full-time and freelance work.</p>
      </footer>
    </div>
  );
}
